import React, { useEffect, useRef, useState } from "react";
import { t } from "../../../i18n";
import { SearchIcon } from "../normalizer";
import InputKeyValue from "../shared/InputKeyValue";
import KeyDropdownValue from "../shared/KeyDropdownValue";

// Delay (ms) before the free-text search actually triggers `onFilterChange`
// (and therefore the wallet items API call), to avoid firing a request on
// every keystroke.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Wallet filter bar: free-text search, date picker, type dropdown and a
 * boolean toggle. Every change bubbles a partial filter object up to the
 * container, which persists it in Redux and re-fetches the items.
 */
const WalletFilters = (props) => {
  const { filters, pills, onFilterChange } = props;

  // Local, immediately-updated copy of the search text so typing feels
  // responsive; the actual `onFilterChange` call (which triggers the API
  // request) is debounced.
  const [searchValue, setSearchValue] = useState(filters.search || "");
  const debounceRef = useRef(null);

  // Stay in sync when the filter changes from elsewhere (e.g. cleared by a
  // "toggle" change, which resets `type`/other filters as a group).
  useEffect(() => {
    setSearchValue(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ search: value });
    }, SEARCH_DEBOUNCE_MS);
  };

  const typeOptions = (pills || []).map((pill) => ({
    id: pill.value !== undefined ? pill.value : (pill.type !== undefined ? pill.type : pill.id),
    name: pill.label || pill.name
  }));

  const selectedType =
    typeOptions.find((option) => option.id === filters.type) || null;

  return (
    <div className="wallet-filters">
      <div className="wallet-filters-search">
        <InputKeyValue
          type="text"
          name="wallet-search"
          leftIcon={<SearchIcon />}
          placeholder={t("gamification2026.wallet.filters.search-placeholder")}
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>
      <div className="wallet-filters-date">
        <InputKeyValue
          type="date"
          name="wallet-date"
          placeholder={t("gamification2026.wallet.filters.date-placeholder")}
          value={filters.date}
          onChange={(date) => onFilterChange({ date: date })}
        />
      </div>
      <div className="wallet-filters-type">
        <KeyDropdownValue
          name="wallet-type"
          isClearable
          options={typeOptions}
          selectedValue={selectedType}
          placeholder={t("gamification2026.wallet.filters.type-placeholder")}
          onChange={(option) => onFilterChange({ type: option ? option.id : "" })}
        />
      </div>
      <label className="wallet-filters-toggle">
        <input
          type="checkbox"
          className="wallet-filters-toggle-input"
          checked={!!filters.toggle}
          onChange={(e) => onFilterChange({ toggle: e.target.checked })}
        />
        <span className={"wallet-filters-toggle-track" + (filters.toggle ? " wallet-filters-toggle-track--active" : "")} />
        <span className="wallet-filters-toggle-label">
          {t("gamification2026.wallet.filters.toggle-label")}
        </span>
      </label>
    </div>
  );
};

export default WalletFilters;
